import React, { useState, useEffect, useMemo } from "react";
import {
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
  Autocomplete,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { Controller, useWatch } from "react-hook-form";
import { Field } from "../types";
import FileUploadDropbox from "../../../dialogs/FileUploadDropbox";

interface FormFieldProps {
  field: Field;
  fieldPath: string;
  control: any;
  register: any;
  errors: any;
  validationErrors: { [key: string]: string };
  handleBlur: (field: Field, fieldPath?: string) => void;
}

// Helper function to check if a field has an error
const hasFieldError = (fieldPath: string, errors: any, validationErrors: any, fieldName: string, fieldType: string) => {
  if (fieldPath.includes(".")) {
    const parts = fieldPath.split(".");
    let current = errors;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (!current?.[part]) return false;
      current = current[part];
    }
    return Boolean(current);
  }
  return Boolean(errors[fieldName] || validationErrors[fieldPath]);
};

const FormField: React.FC<FormFieldProps> = ({ field, fieldPath, control, register, errors, validationErrors, handleBlur }) => {
  const fullFormData = useWatch({ control });

  const [dynamicOptions, setDynamicOptions] = useState<Array<{ value: string; label: string; disabled?: boolean }>>([]);
  const [isLoading, setIsLoading] = useState(false);

  // File upload state (used only for file type fields)
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const dependencyValuesString = useMemo(() => {
    if (!field.dependencies?.length) return "{}";

    const values = field.dependencies.reduce((acc, dep) => {
      const parts = dep.split(".");
      let value = fullFormData;
      try {
        for (const part of parts) {
          value = value?.[part];
        }
        acc[dep] = value;
      } catch (e) {
        acc[dep] = undefined;
      }
      return acc;
    }, {} as Record<string, any>);
    return JSON.stringify(values);
  }, [field.dependencies, fullFormData]);

  useEffect(() => {
    if ((field.type === "select" || field.type === "autocomplete") && typeof field.options === "function") {
      setDynamicOptions([]); // Clear options immediately when deps change
      setIsLoading(true);

      const fetchTimeout = setTimeout(() => {
        if (typeof field.options === "function") {
          field
            .options(fullFormData)
            .then((opts: Array<{ value: string; label: string; disabled?: boolean }>) => {
              setDynamicOptions(opts);
              setIsLoading(false);
            })
            .catch((err: any) => {
              console.error(`Error fetching options for ${fieldPath}:`, err);
              setDynamicOptions([]);
              setIsLoading(false);
            });
        } else {
          setIsLoading(false);
        }
      }, 300);

      return () => clearTimeout(fetchTimeout);
    } else {
      setIsLoading(false);
      if (Array.isArray(field.options)) {
        setDynamicOptions(field.options);
      } else {
        setDynamicOptions([]);
      }
    }
  }, [field.type, field.options, fieldPath, dependencyValuesString]);

  const prevDependencyValuesRef = React.useRef<string>(dependencyValuesString);

  useEffect(() => {
    if (field.validation && field.dependencies && field.dependencies.length > 0) {
      if (prevDependencyValuesRef.current !== dependencyValuesString) {
        prevDependencyValuesRef.current = dependencyValuesString;
        const validationTimeout = setTimeout(() => handleBlur(field, fieldPath), 10);
        return () => clearTimeout(validationTimeout);
      }
    }
  }, [dependencyValuesString, field, fieldPath, handleBlur]);

  const isReadonly = useMemo(() => {
    return typeof field.readonly === "function" ? field.readonly(fullFormData) : field.readonly;
  }, [field.readonly, fullFormData]);

  const error = useMemo(
    () => hasFieldError(fieldPath, errors, validationErrors, field.name, field.type),
    [fieldPath, errors, validationErrors, field.name, field.type]
  );

  const descriptionText = useMemo(() => {
    if (!field.description) return null;
    return typeof field.description === "function" ? field.description(fullFormData) : field.description;
  }, [field.description, fullFormData]);

  const noticeContent = useMemo(() => {
    if (!field.content) return null;
    if (typeof field.content === "function") {
      return field.content(fullFormData);
    }
    return field.content;
  }, [field.content, fullFormData]);

  switch (field.type) {
    case "text":
    case "textarea":
      return (
        <>
          {" "}
          <TextField
            fullWidth
            label={field.label}
            variant="outlined"
            required={field.required}
            disabled={isReadonly}
            multiline={field.type === "textarea"}
            rows={field.type === "textarea" ? 3 : 1}
            error={error || Boolean(validationErrors[fieldPath])}
            helperText={validationErrors[fieldPath] || ""}
            {...register(fieldPath, {
              required: field.required,
              onBlur: field.validation ? () => handleBlur(field, fieldPath) : undefined,
            })}
          />
          {descriptionText && <FormHelperText sx={{ marginLeft: "14px", marginRight: "14px" }}>{descriptionText}</FormHelperText>}
        </>
      );
    case "number":
      return (
        <>
          {" "}
          <TextField
            fullWidth
            label={field.label}
            variant="outlined"
            type="number"
            required={field.required}
            disabled={isReadonly}
            error={error || Boolean(validationErrors[fieldPath])}
            inputProps={field.inputProps || { step: "0.01", min: "0" }}
            InputLabelProps={isReadonly || !!fullFormData?.[fieldPath] ? { shrink: true } : undefined}
            helperText={validationErrors[fieldPath] || ""}
            {...register(fieldPath, {
              required: field.required,
              onBlur: field.validation ? () => handleBlur(field, fieldPath) : undefined,
            })}
          />
          {descriptionText && <FormHelperText sx={{ marginLeft: "14px", marginRight: "14px" }}>{descriptionText}</FormHelperText>}
        </>
      );
    case "select": {
      const optionsToRender = typeof field.options === "function" ? dynamicOptions : Array.isArray(field.options) ? field.options : [];

      return (
        <FormControl
          fullWidth
          variant="outlined"
          required={field.required}
          disabled={isReadonly || isLoading}
          error={error || Boolean(validationErrors[fieldPath])}
        >
          <InputLabel id={`${fieldPath}-label`}>{field.label}</InputLabel>
            <Controller
              name={fieldPath}
              control={control}
              defaultValue=""
              rules={{ required: field.required }}
              render={({ field: controllerField }) => {
                // Check if the current value exists in options
                const currentValue = controllerField.value || "";
                const valueExistsInOptions = currentValue === "" || optionsToRender.some(opt => opt.value === currentValue);
                const displayValue = (isLoading || !valueExistsInOptions) ? "" : currentValue;
                
                return (
                  <Select
                    labelId={`${fieldPath}-label`}
                    label={field.label}
                    error={error || Boolean(validationErrors[fieldPath])}
                    {...controllerField}
                    onBlur={(e) => {
                      controllerField.onBlur();
                      if (field.validation) {
                        handleBlur(field, fieldPath);
                      }
                    }}
                    value={displayValue}
                    onChange={(e) => {
                      controllerField.onChange(e.target.value);
                    }}
                  >
                {isLoading && (
                  <MenuItem disabled value="">
                    <CircularProgress size={20} /> Loading...
                  </MenuItem>
                )}
                {!isLoading && optionsToRender.length === 0 && (
                  <MenuItem disabled value="">
                    {typeof field.options === "function" ? "No options available" : "No options defined"}
                  </MenuItem>
                )}
                {!isLoading &&
                  optionsToRender.map((option) => (
                    <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              );
            }}
          />
          {(descriptionText || validationErrors[fieldPath]) && (
            <FormHelperText error={!!validationErrors[fieldPath]}>{validationErrors[fieldPath] || descriptionText}</FormHelperText>
          )}
        </FormControl>
      );
    }
    case "checkbox":
      return (
        <div style={isReadonly ? { pointerEvents: "none", opacity: 0.7 } : {}}>
          <FormControlLabel
            control={
              <Controller
                name={fieldPath}
                control={control}
                defaultValue={false}
                render={({ field: controllerField }) => (
                  <Checkbox
                    checked={!!controllerField.value}
                    onChange={(e) => controllerField.onChange(e.target.checked)}
                    onBlur={() => {
                      if (field.validation) {
                        handleBlur(field, fieldPath);
                      }
                    }}
                    required={field.required}
                  />
                )}
              />
            }
            label={field.label}
          />
          {(descriptionText || validationErrors[fieldPath]) && (
            <FormHelperText error={!!validationErrors[fieldPath]} style={{ marginTop: -5, marginLeft: 14 }}>
              {validationErrors[fieldPath] || descriptionText}
            </FormHelperText>
          )}
        </div>
      );
    case "date":
      return (
        <>
          {" "}
          <TextField
            fullWidth
            label={field.label}
            variant="outlined"
            type="date"
            required={field.required}
            disabled={isReadonly}
            error={error || Boolean(validationErrors[fieldPath])}
            helperText={validationErrors[fieldPath] || ""}
            InputLabelProps={{ shrink: true }}
            {...register(fieldPath, {
              required: field.required,
              onBlur: field.validation ? () => handleBlur(field, fieldPath) : undefined,
            })}
          />
          {descriptionText && <FormHelperText sx={{ marginLeft: "14px", marginRight: "14px" }}>{descriptionText}</FormHelperText>}
        </>
      );
    case "autocomplete": {
      const optionsToRender = typeof field.options === "function" ? dynamicOptions : Array.isArray(field.options) ? field.options : [];

      return (
        <FormControl
          fullWidth
          variant="outlined"
          required={field.required}
          disabled={isReadonly || isLoading}
          error={error || Boolean(validationErrors[fieldPath])}
        >
          <Controller
            name={fieldPath}
            control={control}
            defaultValue=""
            rules={{ required: field.required }}
            render={({ field: controllerField }) => (
              <Autocomplete
                options={optionsToRender}
                loading={isLoading}
                disabled={isReadonly || isLoading}
                getOptionLabel={(option) => {
                  // Handle both string values and option objects
                  if (typeof option === "string") {
                    const foundOption = optionsToRender.find((opt) => opt.value === option);
                    return foundOption ? foundOption.label : option;
                  }
                  return option.label || "";
                }}
                isOptionEqualToValue={(option, value) => {
                  if (typeof value === "string") {
                    return option.value === value;
                  }
                  return option.value === value?.value;
                }}
                value={
                  controllerField.value
                    ? typeof controllerField.value === "string"
                      ? optionsToRender.find((opt) => opt.value === controllerField.value) || null
                      : controllerField.value
                    : null
                }
                onChange={(_, newValue) => {
                  controllerField.onChange(newValue ? newValue.value : "");
                }}
                onBlur={() => {
                  controllerField.onBlur();
                  if (field.validation) {
                    handleBlur(field, fieldPath);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={field.label}
                    required={field.required}
                    error={error || Boolean(validationErrors[fieldPath])}
                    helperText={validationErrors[fieldPath] || ""}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            )}
          />
          {descriptionText && !validationErrors[fieldPath] && <FormHelperText>{descriptionText}</FormHelperText>}
        </FormControl>
      );
    }
    case "file": {
      return (
        <Controller
          name={fieldPath}
          control={control}
          defaultValue={[]}
          rules={{ required: field.required }}
          render={({ field: controllerField }) => {
            const handleUploadSuccess = (data: any) => {
              const fieldName = fieldPath.split(".").pop(); // Get the last part of the path (e.g., 'poa_proof')
              if (fieldName && data[fieldName]) {
                const newFiles = Array.isArray(data[fieldName]) ? data[fieldName] : [data[fieldName]];
                const processedFiles = newFiles.map((fileData: any) => {
                  return typeof fileData === "string" ? fileData : fileData.original_path;
                });

                const currentFiles = Array.isArray(controllerField.value) ? controllerField.value : [];
                const updatedFiles = [...currentFiles, ...processedFiles];
                controllerField.onChange(updatedFiles);
                setUploadedFiles(updatedFiles);
                setIsUploading(false);
              }
            };

            const handleUploadError = (error: any) => {
              console.error("File upload error:", error);
              setIsUploading(false);
              if (error.response?.data?.message) {
                alert(error.response.data.message);
              } else {
                alert("ფაილის ატვირთვისას დაფიქსირდა შეცდომა");
              }
            };

            const handleFilesSelected = (files: File[]) => {
              setIsUploading(true);
            };

            const currentFiles = Array.isArray(controllerField.value) ? controllerField.value : [];

            return (
              <Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                  {field.label}
                  {field.required && <span style={{ color: "red" }}> *</span>}
                </Typography>

                {currentFiles.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      ატვირთული ფაილები:
                    </Typography>
                    {currentFiles.map((filePath: string, index: number) => (
                      <Typography key={index} variant="body2" sx={{ ml: 2 }}>
                        • {filePath.split("/").pop()}
                      </Typography>
                    ))}
                  </Box>
                )}

                <FileUploadDropbox
                  uploadUrl="/bank/application/upload_attachments/new-universal"
                  onUploadSuccess={handleUploadSuccess}
                  onUploadError={handleUploadError}
                  fieldName={fieldPath.split(".").pop() || "file"}
                  promptMessage="გსურთ ფაილის ატვირთვა?"
                  multiple={false}
                  onFilesSelected={handleFilesSelected}
                />

                {isUploading && (
                  <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    <Typography variant="body2">იტვირთება...</Typography>
                  </Box>
                )}

                {validationErrors[fieldPath] && (
                  <FormHelperText error sx={{ mt: 1 }}>
                    {validationErrors[fieldPath]}
                  </FormHelperText>
                )}

                {descriptionText && !validationErrors[fieldPath] && <FormHelperText sx={{ mt: 1 }}>{descriptionText}</FormHelperText>}
              </Box>
            );
          }}
        />
      );
    }
    case "notice":
      // Don't render if content is empty
      if (!noticeContent) return null;

      return (
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              p: 2,
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
              border: "1px solid #e0e0e0",
            }}
          >
            {typeof noticeContent === "string" ? <div dangerouslySetInnerHTML={{ __html: noticeContent }} /> : noticeContent}
          </Box>
          {descriptionText && <FormHelperText sx={{ mt: 1 }}>{descriptionText}</FormHelperText>}
        </Box>
      );
    default:
      return null;
  }
};

export default React.memo(FormField);
